import { Component, DestroyRef, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { ThemeService } from '@core/services/theme.service';
import { PAGE_TITLES, usePageTitle } from '@core/utils/page-title.util';
import { SurveyPageComponent } from '@feature/survey-management/components/survey-page/survey-page.component';
import { AnswerModuleResponse } from '@shared/models/answer-module-response';
import { PreviewMode } from '@shared/models/enums/preview-mode.enum';
import { ThemeParameters } from '@shared/models/themes/theme-config';
import { View } from '@shared/models/view';
import { switchMap } from 'rxjs';

const MESSAGE_TYPES = {
  UPDATE_THEME_PARAMETERS: 'updateThemeParameters',
} as const;

const QUERY_PARAMS = {
  THEME_PARAMETERS: 'themeParameters',
} as const;

const THEME_PREVIEW_DEFAULTS = {
  THEME_ID: '',
  ENABLE_PROGRESS_BAR: true,
  PERCENT_PROGRESS: 10,
  IS_LAST_PAGE: false,
} as const;

interface ThemeMessage {
  type: typeof MESSAGE_TYPES.UPDATE_THEME_PARAMETERS;
  themeParameters: ThemeParameters;
  customerId?: string;
}

@Component({
  selector: 'app-theme-preview',
  imports: [SurveyPageComponent],
  templateUrl: './theme-preview.component.html',
  styleUrl: './theme-preview.component.scss',
})
export class ThemePreviewComponent implements OnInit {
  response?: AnswerModuleResponse;
  view?: View;
  private setPageTitle = usePageTitle();
  private messageHandler = (event: MessageEvent) => {
    // Security: Validate message origin if needed
    // if (event.origin !== 'https://trusted-domain.com') return;
    try {
      const message = this.parseMessage(event.data);

      if (
        message?.type === MESSAGE_TYPES.UPDATE_THEME_PARAMETERS &&
        message.themeParameters
      ) {
        this.updateThemeParameters(message.themeParameters, message.customerId);
      }
    } catch (error) {
      console.error('Error processing message:', error);
    }
  };

  constructor(
    private route: ActivatedRoute,
    private themeService: ThemeService,
    private destroyRef: DestroyRef,
  ) {}

  ngOnInit(): void {
    this.setPageTitle(PAGE_TITLES.THEME.key, PAGE_TITLES.THEME.fallback);
    this.setupMessageListener();
    this.getThemePreview();
  }

  private parseMessage(data: unknown): ThemeMessage | null {
    try {
      const message = typeof data === 'string' ? JSON.parse(data) : data;
      return message as ThemeMessage;
    } catch {
      return null;
    }
  }

  private updateThemeParameters(
    themeParameters: ThemeParameters,
    customerId?: string,
  ): void {
    if (this.themeService.highContrast) {
      this.themeService.toggleHighContrast();
    }
    this.themeService.syncQuestWidthToHighContrastTheme(themeParameters);
    this.themeService.loadThemeParameters(themeParameters, customerId);
  }

  private setupMessageListener(): void {
    window.addEventListener('message', this.messageHandler);

    this.destroyRef.onDestroy(() => {
      window.removeEventListener('message', this.messageHandler);
    });
  }

  private getThemePreview(): void {
    this.route.queryParamMap
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(() => this.themeService.getThemeSurvey()),
      )
      .subscribe({
        next: (response) => {
          this.initializeResponse(response);
          this.loadThemeFromQueryParams();
        },
        error: (error) => {
          console.error('Error loading theme preview:', error);
          this.themeService.loadTheme(THEME_PREVIEW_DEFAULTS.THEME_ID);
        },
      });
  }

  private initializeResponse(response: AnswerModuleResponse): void {
    response.themeId = THEME_PREVIEW_DEFAULTS.THEME_ID;
    response.enableProgressBar = THEME_PREVIEW_DEFAULTS.ENABLE_PROGRESS_BAR;
    response.percentProgress = THEME_PREVIEW_DEFAULTS.PERCENT_PROGRESS;
    response.isLastPage = THEME_PREVIEW_DEFAULTS.IS_LAST_PAGE;

    this.response = response;
    this.view = response.view;
  }

  private loadThemeFromQueryParams(): void {
    const queryParams = this.route.snapshot.queryParamMap;
    const themeParametersJson =
      queryParams.get(QUERY_PARAMS.THEME_PARAMETERS) || '';

    try {
      const themeParameters: ThemeParameters = JSON.parse(themeParametersJson);
      const customerId = queryParams.get('customerId') || undefined;
      this.themeService.loadThemeParameters(themeParameters, customerId);
    } catch (error) {
      console.error('Error parsing theme parameters:', error);
      this.themeService.loadTheme(THEME_PREVIEW_DEFAULTS.THEME_ID); // Fallback to high contrast
    }
  }

  protected readonly PreviewMode = PreviewMode;
}
